import { environment } from '../../../../../../../environments/environment';
import { list } from '../../../../../../common/classes/request-builder';
import { StorageManager } from '../../../../../../common/storage-manager.class';

import { ClienteModel } from '../../../../../../models/cliente.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';
import { SectorModel } from 'src/app/models/form-common/sector.model';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { ProductModel } from 'src/app/models/productos/product.model';
import { MantenimientoModel } from 'src/app/models/trabajadores/mantenimiento.model';
import { UserModel } from 'src/app/models/usuarios/user.model';
import {
    DropdownData,
    FilteredValue,
    Input,
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    type ReactiveDropdownDataSourceContext,
} from '@fitogest/reactive-form-builder/interfaces';
import { TareaOtraModel } from '../../../../../../models/tareas/tarea-otra.model';
import {
    getDropdownSelectedArray,
    getDropdownSelectedSingle,
} from '@fitogest/reactive-form-builder/utils';
import {
    createClientAndFincaScopedParams,
    createClientScopedParams,
    createTareasLocationDropdownDataSource,
    getContextEntityId,
    getContextEntityIds,
    tareasLocationDependencyBehaviour,
} from '../../../shared/tareas-location-dropdown.datasource';

type OtrasCalendarEvent = {
    start: string;
    title: string;
    hora_inicio: string;
    hora_fin: string;
};

type OtrasFieldOptions = {
    name: string;
    label: string;
    inputType: InputType;
    data?: DropdownData<unknown>;
    defaultValue?: string | boolean | number | Date | TareaOtraModel | null;
    isEditable?: boolean;
    isRequired?: boolean;
    isVisible?: boolean;
    isRememberedOnDuplicate?: boolean;
    width: number;
    order: number;
    valuePrimaryKey?: string;
    vinculatedFields?: string[];
    filter?: (it: FilteredValue<unknown>) => boolean;
    style?: string;
    calendarConfiguration?: Input<TareaOtraModel>['calendarConfiguration'];
    textAreaConfiguration?: Input<TareaOtraModel>['textAreaConfiguration'];
    textMaskConfiguration?: Input<TareaOtraModel>['textMaskConfiguration'];
    dropdownConfiguration?: Input<TareaOtraModel>['dropdownConfiguration'];
    updateValue?: (it: FormField<TareaOtraModel>) => TareaOtraModel | string | null | undefined;
    computed?: FormField<TareaOtraModel>['computed'];
    dependencies?: FormField<TareaOtraModel>['dependencies'];
};

export class OtrasFormDefinition {
    public readonly rolUser = StorageManager.getUser().rol;
    public readonly hasClients = environment.features.hasClients;
    public readonly clienteName = environment.features.clienteName;
    public readonly fincaName = environment.features.fincaName;
    public readonly fincasName = environment.features.fincasName;
    public readonly showSectores = environment.features.showSectores;
    public readonly sectorName = environment.features.sectorName;
    public readonly sectoresName = environment.features.sectoresName;
    public readonly showParcelas = environment.features.showParcelas;
    public readonly parcelaName = environment.features.parcelaName;
    public readonly parcelasName = environment.features.parcelasName;
    public readonly tipoSuperficieParcelas = environment.features.hasSuperficieCultivada ? 'Sup.\xa0Cultivada' : 'S.\xa0Sigpac';
    public readonly textoSuperficie = environment.features.textoSuperficie;
    public readonly showTareasGenericasObservaciones = environment.features.showTareasGenericasObservaciones;
    public readonly showTareasOtrosDuracion = environment.features.showTareasOtrosDuracion;
    public readonly showTareasOtrasProducto = environment.features.showTareasOtrasProducto;
    public readonly showTareasOtrasCantidad = environment.features.showTareasOtrasCantidad;
    public readonly showTareasOtrasUnidadMedida = environment.features.showTareasOtrasUnidadMedida;
    public readonly showTareasOtrosMultiple = environment.features.showTareasOtrosMultiple;
    public readonly showTareasOtrosImagen = environment.features.showTareasOtrosImagen;
    public readonly showTareasProcura = environment.features.showTareasProcura;
    public readonly appName = environment.appName;
    public readonly applicationType = environment.features.applicationType;
    public readonly serveUrl = environment.serverUrl;
    public readonly timeStartStopTareas = environment.features.timeStartStopTareas;
    public readonly canSeeClientes = this.hasClients && (environment.features.userCanSeeClientes || StorageManager.getUser().rol === 'admin');

    public disabledDates: Date[];
    public formType: FormType = FormType.CREATION;
    public model: Partial<TareaOtraModel> = {};
    public formFields!: Form<TareaOtraModel>;
    public formFieldsReactive!: Form<TareaOtraModel>;
    public fieldNames: FormField<TareaOtraModel>[] = [];
    public fieldLabels: string[] = [];

    public clientes: DropdownData<ClienteModel> = list<ClienteModel>();
    public fincas: DropdownData<FincasModel> = list<FincasModel>();
    public sectores: DropdownData<SectorModel> = list<SectorModel>();
    public parcelas: DropdownData<ParcelasModel> = list<ParcelasModel>();
    public users: DropdownData<UserModel> = list<UserModel>();
    public maquinarias: DropdownData<MaquinariaModel> = list<MaquinariaModel>();
    public trabajadores: DropdownData<MantenimientoModel> = list<MantenimientoModel>();
    public tareas: DropdownData<{ id?: string | number; nombre?: string }> = list<{ id?: string | number; nombre?: string }>();
    public productos: DropdownData<ProductModel> = list<ProductModel>(['...', null]);
    public estadoTarea: DropdownData<string> = list<string>(['...', null], 'Orden de Trabajo', 'Parte de Trabajo');
    public prioridades: DropdownData<string> = list<string>(['...', null], 'Alta', 'Realizada');

    public readonly activateRequireHourForRolUser = this.appName === 'ava' ? StorageManager.getUser().rol !== 'admin' : false;

    public actividadAgraria: DropdownData<string> = list<string>(
        ['...', null],
        'Producción en Tierras de Cultivo y Cultivos Permanentes',
        'Mantenimiento',
        'Pastoreo',
        'Siega Producción',
        'Sin actividad agraria por razones medioambientales',
    );

    public tareasMaquinaria: DropdownData<string> = list<string>(
        ['...', null],
        'Abonar',
        'Fangueo',
        'Hacer Márgenes',
        'Trailla',
        'Láser',
        'Cultivador',
        'Rotovator',
        'Multiapero',
        'Paquete de trabajo 1',
        'Paquete de trabajo 2',
    );

    public tareasManuales: DropdownData<string> = list<string>(
        ['...', null],
        'Abrir Boqueras',
        'Bordoma',
        'Transplantar',
        'Escarda',
        'Desbroce Cursos de Agua',
        'Siega Orillas',
        'Quemar Paja',
    );

    constructor(
        disabledDates: Date[],
        private readonly eventos: OtrasCalendarEvent[],
        type: FormType = FormType.CREATION,
        model: Partial<TareaOtraModel> = {},
    ) {
        this.disabledDates = disabledDates;
        this.formType = +type;
        this.model = this.normalizeModelDates(model);
        this.rebuildReactiveForm();
    }

    public addClientFieldForDuplicate(): void {
        this.rebuildReactiveForm();
    }

    public rebuildReactiveForm(): void {
        this.formFields = {
            name: 'tareas-otros',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: this.showTareasOtrosImagen,
                label: 'Añadir foto',
                imageName: 'adjunto',
                imageFolder: 'tareas_otros',
                imageDisplay: 'link',
                viewFolder: 'tareas_otros',
                accept: 'image/*',
                allowedExtensions: ['gif', 'jpeg', 'jpg', 'jiff', 'png', 'svg', 'tiff'],
                invalidFormatMessage: 'Formato de imagen no válido.',
            },
            signed: false,
            serializer: {
                fields: {
                    fecha: { mode: 'date', dateFormat: 'spanish' },
                    fecha_fin: { mode: 'date', dateFormat: 'spanish' },
                },
            },
        };
        this.formFieldsReactive = this.formFields;

        this.fieldNames = this.formFields.sections
            .filter((section) => section.order !== 6)
            .flatMap((section) => section.fields)
            .filter((field) => field.conditions.isVisible && field.input.type !== InputType.EMPTY);
        this.fieldLabels = this.fieldNames.map((field) => field.label);
    }


    public setLocationDataSources(endpoints: { fincas: unknown; sectores: unknown; parcelas: unknown }): void {
        const fincaField = this.getReactiveField(this.formType < 3 ? 'id_finca' : 'finca');
        const sectorField = this.getReactiveField(this.formType < 3 ? 'id_sector' : 'sector');
        const parcelaField = this.getReactiveField(this.formType < 3 ? 'ids_parcelas' : 'parcelas');

        if (fincaField !== undefined) {
            fincaField.dataSource = createTareasLocationDropdownDataSource<TareaOtraModel, FincasModel>({
                endpoint: endpoints.fincas,
                cacheKey: 'tareas-otras-fincas',
                label: 'nombre',
                ...(this.canSeeClientes ? { dependsOn: ['id_cliente'] } : {}),
                params: createClientScopedParams,
                filterOption: (option) => this.filterFinca({ label: String(option.label ?? ''), value: option.value as FincasModel }),
            });
            fincaField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }

        if (sectorField !== undefined) {
            sectorField.dataSource = createTareasLocationDropdownDataSource<TareaOtraModel, SectorModel>({
                endpoint: endpoints.sectores,
                cacheKey: 'tareas-otras-sectores',
                label: 'nombre',
                dependsOn: ['id_cliente', 'id_finca'],
                params: createClientAndFincaScopedParams,
                filterOption: (option, context) => this.isSectorOptionForSelectedFinca(option.value, context),
            });
            sectorField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }

        if (parcelaField !== undefined) {
            parcelaField.dataSource = createTareasLocationDropdownDataSource<TareaOtraModel, ParcelasModel>({
                endpoint: endpoints.parcelas,
                cacheKey: 'tareas-otras-parcelas',
                label: 'nombre',
                dependsOn: ['id_cliente', 'id_finca', 'id_sector'],
                params: createClientAndFincaScopedParams,
                filterOption: (option, context) => this.isParcelaOptionForSelectedLocation(option.value, context),
            });
            parcelaField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }
    }

    public updateReactiveParcelasLabel(label: string): void {
        const field = this.getReactiveField('ids_parcelas');

        if (field !== undefined) {
            field.label = label;
        }
    }

    public getReactiveField(fieldName: string): FormField<TareaOtraModel> | undefined {
        return this.formFields.sections
            .flatMap((section) => section.fields)
            .find((field) => field.name === fieldName);
    }

    public calculateHorasTotales(): string {
        const horaInicio = this.normalizeTimeValue(this.model.hora_inicio);
        const horaFin = this.normalizeTimeValue(this.model.hora_fin);

        if (horaInicio.length === 0 || horaFin.length === 0) {
            return '';
        }

        const inicio = this.timeToMinutes(horaInicio);
        const fin = this.timeToMinutes(horaFin);

        if (inicio === null || fin === null || fin < inicio) {
            return '';
        }

        return this.minutesToTime(fin - inicio);
    }

    private formatHoursMinutes(startTime: string, endTime: string): string {
        if (startTime.length === 0 || endTime.length === 0) {
            return '';
        }

        const startMinutes = this.timeToMinutes(startTime);
        const endMinutes = this.timeToMinutes(endTime);

        if (startMinutes === null || endMinutes === null || endMinutes < startMinutes) {
            return '';
        }

        return this.minutesToTime(endMinutes - startMinutes);
    }

    public esViveroOGardenGest(): boolean {
        return this.serveUrl.includes('gardengest') || this.serveUrl.includes('viverogest');
    }

    private getHeader(): string {
        if (this.formType === FormType.EDITION) {
            return 'Actualizar Tarea';
        }

        if (this.formType === FormType.DUPLICATION) {
            return 'Duplicar Tarea';
        }

        return 'Realizar Tarea Genérica';
    }

    private getFormSections(): FormSection<TareaOtraModel>[] {
        const sections: FormSection<TareaOtraModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getLocationFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getDateTaskAndWorkerFields(),
                order: 2,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getOtherTaskFields(),
                order: 3,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHoursFields(),
                order: 4,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getObservationFields(),
                order: 5,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 6,
            },
        ];

        return sections.sort((a, b) => a.order - b.order);
    }

    private getLocationFields(): FormField<TareaOtraModel>[] {
        return this.registerFields([
            this.createField({
                name: this.formType < 3 ? 'id_cliente' : 'cliente_nombre',
                label: this.clienteName,
                inputType: InputType.DROPDOWN_SEARCH,
                data: this.clientes as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                defaultValue: this.formType < FormType.DELETION ? this.getSelectedClientId() : null,
                isRequired: true,
                isVisible: this.shouldShowClientSelector(),
                width: 3,
                order: 1,
            }),
            this.createField({
                name: this.formType < 3 ? 'id_finca' : 'finca',
                label: this.fincaName,
                inputType: InputType.DROPDOWN_SEARCH,
                data: this.fincas as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                vinculatedFields: ['id_cliente'],
                filter: (it) => this.filterFinca(it as FilteredValue<FincasModel>),
                isRequired: true,
                width: 3,
                order: 2,
            }),
            this.createField({
                name: this.formType < 3 ? 'id_sector' : 'sector',
                label: this.sectorName,
                inputType: this.showTareasOtrosMultiple ? InputType.MULTISELECT_SEARCH : InputType.DROPDOWN_SEARCH,
                data: this.sectores as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                vinculatedFields: ['id_finca'],
                filter: (it) => this.filterSector(it as FilteredValue<SectorModel>),
                isVisible: this.showSectores,
                width: 3,
                order: 3,
            }),
            this.createField({
                name: this.formType < 3 ? 'ids_parcelas' : 'parcelas',
                label: this.parcelaName,
                inputType: InputType.MULTISELECT_SEARCH,
                data: this.parcelas as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < 3 ? 'ids_parcelas' : 'parcelas',
                        kind: 'filter'
                    },
                    {
                        source: 'id_sector',
                        target: this.formType < 3 ? 'ids_parcelas' : 'parcelas',
                        kind: 'filter'
                    }
                ],
                filter: (it) => this.filterParcela(it as FilteredValue<ParcelasModel>),
                isRequired: this.appName !== 'pyf',
                isVisible: this.showParcelas,
                style: 'min-height: 29px !important;',
                dropdownConfiguration: {
                    summation: {
                        summand: environment.features.hasSuperficieCultivada ? 'superficie_cultivada' : 'superficie_sigpac',
                        name: this.tipoSuperficieParcelas,
                        uds: this.textoSuperficie,
                        format: (total: number): string => total.toLocaleString('es-ES', {
                            useGrouping: true,
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }),
                    },
                },
                width: 3,
                order: 4,
            }),
        ]);
    }

    private getDateTaskAndWorkerFields(): FormField<TareaOtraModel>[] {
        return this.registerFields([
            this.createField({
                name: 'fecha',
                label: 'Fecha',
                inputType: InputType.CALENDAR,
                calendarConfiguration: {
                    calendarView: 'date',
                    calendarDateFormat: 'dd/mm/yy',
                    disabledDates: this.disabledDates,
                    onSelect: (date: Date) => this.showDetorresEvents(date),
                },
                isRequired: this.appName !== 'ava',
                width: 3,
                order: 1,
            }),
            this.createField({
                name: this.formType < 3 ? 'id_tarea' : 'tarea',
                label: this.appName === 'detorres'
                    ? 'Jardinería'
                    : (this.esViveroOGardenGest() ? 'Genéricas' : 'Tareas'),
                inputType: InputType.DROPDOWN_SEARCH,
                data: this.tareas as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                isVisible: this.appName !== 'sequiador' && this.appName !== 'SIEX',
                width: 3,
                order: 2,
            }),
            this.createField({
                name: this.formType < 3 ? 'ids_trabajadores' : 'trabajadores_nombre',
                label: 'Trabajadores',
                inputType: InputType.MULTISELECT_SEARCH,
                data: this.trabajadores as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                filter: (it) => this.filterTrabajador(it as FilteredValue<MantenimientoModel>),
                isVisible: (this.appName === 'tomega' && this.rolUser !== 'aplicador') || this.appName !== 'tomega',
                width: 3,
                order: 3,
            }),
            this.createField({
                name: this.formType < 3 ? 'id_maquinaria' : 'maquinaria',
                label: 'Maquinaria',
                inputType: InputType.DROPDOWN_SEARCH,
                data: this.maquinarias as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                filter: (it) => this.filterMaquinaria(it as FilteredValue<MaquinariaModel>),
                width: 3,
                order: 4,
            }),
        ]);
    }

    private getOtherTaskFields(): FormField<TareaOtraModel>[] {
        return this.registerFields([
            this.createField({
                name: 'fecha_fin',
                label: 'Fecha Fin',
                inputType: InputType.CALENDAR,
                calendarConfiguration: {
                    calendarView: 'date',
                    calendarDateFormat: 'dd/mm/yy',
                },
                isVisible: this.appName === 'fotoagricultura',
                width: 3,
                order: 1,
            }),
            this.createField({
                name: this.formType < 3 ? 'id_producto' : 'producto',
                label: 'Producto',
                inputType: InputType.DROPDOWN_SEARCH,
                data: this.productos as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                filter: (it) => this.filterProducto(it as FilteredValue<ProductModel>),
                isVisible: this.showTareasOtrasProducto,
                width: 3,
                order: 2,
            }),
            this.createField({
                name: 'cantidad',
                label: 'Cantidad',
                inputType: InputType.NUMERIC,
                isVisible: this.showTareasOtrasCantidad,
                width: 3,
                order: 3,
            }),
            this.createField({
                name: 'unidad_medida',
                label: 'Unidad de Medida',
                inputType: InputType.DROPDOWN,
                data: list<unknown>(),
                isVisible: this.showTareasOtrasUnidadMedida,
                width: 3,
                order: 4,
            }),
            this.createField({ name: 'num_albaran', label: 'Nº albarán', inputType: InputType.TEXT, isVisible: this.appName === 'pyf', width: 3, order: 5 }),
            this.createField({ name: 'apero', label: 'Apero utilizado', inputType: InputType.TEXT, isVisible: this.appName === 'pyf', width: 3, order: 6 }),
            this.createField({
                name: 'pastoreo',
                label: 'Pastoreo',
                inputType: InputType.DROPDOWN_SEARCH,
                data: list<string>(['...', null], 'Si', 'No') as DropdownData<unknown>,
                isVisible: this.appName === 'pyf',
                width: 3,
                order: 7,
            }),
            this.createField({
                name: 'procura',
                label: 'Procura',
                inputType: InputType.DROPDOWN_SEARCH,
                data: list<string>(['...', null], 'Procura', 'Media procura', 'Libre') as DropdownData<unknown>,
                isVisible: this.showTareasProcura,
                width: 3,
                order: 8,
            }),
            this.createField({
                name: 'obs',
                label: 'Observaciones',
                inputType: InputType.DROPDOWN_SEARCH,
                data: list<string>(['...', null], 'Total', 'Parcial', 'Por bandas') as DropdownData<unknown>,
                isVisible: this.appName === 'pyf',
                width: 3,
                order: 9,
            }),
            this.createField({
                name: this.formType < 3 ? 'ids_asignados' : 'nombres_asignados',
                label: 'Asignador',
                inputType: InputType.MULTISELECT,
                data: this.users as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                isRequired: true,
                isVisible: (environment.features.showAssignUserTasksFertilizante || environment.features.showAssignUserInAllTasks) &&
                    StorageManager.getUser().rol === 'admin',
                width: 3,
                order: 10,
            }),
            this.createField({
                name: 'horas',
                label: this.appName === 'detorres' ? 'Horas Maquinaria' : 'Horas',
                inputType: (this.appName === 'fotoagricultura' || this.appName === 'agropedbel') ? InputType.TEXT : InputType.TIME,
                isVisible: !this.timeStartStopTareas && this.appName !== 'avafitogest',
                width: 3,
                order: 11,
            }),
            this.createField({
                name: 'estado_tarea',
                label: 'Estado de la Tarea',
                inputType: InputType.DROPDOWN,
                data: this.estadoTarea as DropdownData<unknown>,
                isVisible: this.appName === 'onda' || this.appName === 'lacooperativa',
                width: 3,
                order: 12,
            }),
            this.createField({
                name: 'empresa_pagadora',
                label: 'Empresa Pagadora',
                inputType: InputType.TEXT,
                isVisible: this.appName === 'onda' || this.appName === 'lacooperativa',
                width: 3,
                order: 13,
            }),
            this.createField({
                name: 'actividad_agraria',
                label: 'Actividad Agraria',
                inputType: InputType.DROPDOWN,
                data: this.actividadAgraria as DropdownData<unknown>,
                isVisible: (this.appName === 'sequiador' || environment.features.activateSIEX) && this.appName !== 'agrotata',
                width: 3,
                order: 14,
            }),
            this.createField({
                name: 'tareas_maquinaria',
                label: 'Tareas Maquinaria',
                inputType: InputType.DROPDOWN,
                data: this.tareasMaquinaria as DropdownData<unknown>,
                isVisible: (this.appName === 'sequiador' || environment.features.activateSIEX) && this.appName !== 'agrotata',
                width: 3,
                order: 15,
            }),
            this.createField({
                name: 'tareas_manuales',
                label: 'Tareas Manuales',
                inputType: InputType.DROPDOWN,
                data: this.tareasManuales as DropdownData<unknown>,
                isVisible: (this.appName === 'sequiador' || environment.features.activateSIEX) && this.appName !== 'agrotata',
                width: 3,
                order: 16,
            }),
            this.createField({
                name: 'prioridad',
                label: 'Prioridad',
                inputType: InputType.DROPDOWN,
                data: list<string>(['...', null], 'Alta', 'Media', 'Baja') as DropdownData<unknown>,
                isVisible: this.appName === 'ava',
                width: 3,
                order: 17,
            }),
            this.createField({
                name: 'aplicado',
                label: 'Aplicado',
                inputType: InputType.DROPDOWN,
                data: list<string>(['...', null], 'Si', 'No') as DropdownData<unknown>,
                isVisible: this.appName === 'ava',
                width: 3,
                order: 18,
            }),
            this.createField({
                name: 'prioridad',
                label: 'Prioridad/Realizada',
                inputType: InputType.DROPDOWN,
                data: this.prioridades as DropdownData<unknown>,
                isVisible: environment.features.showTareasMto,
                width: 3,
                order: 19,
            }),
        ]);
    }

    private getHoursFields(): FormField<TareaOtraModel>[] {
        return this.registerFields([
            this.createField({
                name: 'hora_inicio',
                label: 'Hora Inicio',
                inputType: InputType.TIME,
                isRequired: this.activateRequireHourForRolUser || this.appName === 'agrotata',
                isVisible: this.timeStartStopTareas,
                width: 3,
                order: 1,
            }),
            this.createField({
                name: 'hora_fin',
                label: 'Hora Fin',
                inputType: InputType.TIME,
                isRequired: this.activateRequireHourForRolUser || this.appName === 'agrotata',
                isVisible: this.timeStartStopTareas,
                width: 3,
                order: 2,
            }),
            this.createField({
                name: 'horas_totales',
                label: 'Horas Totales',
                inputType: InputType.ONLY_TEXT,
                vinculatedFields: ['hora_inicio', 'hora_fin'],
                computed: {
                    dependsOn: ['hora_inicio', 'hora_fin'],
                    resolve: (context) => this.formatHoursMinutes(
                        this.normalizeTimeValue(context.getValue('hora_inicio')),
                        this.normalizeTimeValue(context.getValue('hora_fin')),
                    ),
                },
                updateValue: () => this.calculateHorasTotales(),
                isVisible: environment.features.showTareasMto || environment.features.tareasHorasTotales || this.showTareasOtrosDuracion,
                width: 3,
                order: 3,
            }),
        ]);
    }

    private getObservationFields(): FormField<TareaOtraModel>[] {
        return this.registerFields([
            this.createField({
                name: 'observaciones',
                label: 'Observaciones',
                inputType: InputType.TEXTAREA,
                textAreaConfiguration: {
                    hasVoiceTranscription: true,
                    canTakePhoto: false,
                    photoField: undefined,
                },
                isVisible: this.showTareasGenericasObservaciones,
                width: 12,
                order: 1,
            }),
        ]);
    }

    private getHiddenFields(): FormField<TareaOtraModel>[] {
        return this.registerFields([
            this.createHiddenField('id', this.model.id ?? null, 1, false),
            this.createHiddenField('id_cliente', this.getSelectedClientId(), 2, true, !this.shouldShowClientSelector()),
            this.createHiddenField('id_usuario', this.model.id_usuario ?? StorageManager.getUser()?.id ?? null, 3, true),
            this.createHiddenField('cultivo', this.model.cultivo ?? '', 4, true),
            this.createHiddenField('variedad', this.model.variedad ?? '', 5, true),
            this.createHiddenField('sup_cultivada', this.model.sup_cultivada ?? '', 6, true),
            this.createHiddenField('parcelas_superficie', this.model.parcelas_superficie ?? '', 7, true),
            this.createHiddenField('adjunto', this.model.adjunto ?? '', 8, true),
        ]);
    }

    private registerFields(fields: FormField<TareaOtraModel>[]): FormField<TareaOtraModel>[] {
        return fields.sort((a, b) => a.order - b.order);
    }

    private createField(options: OtrasFieldOptions): FormField<TareaOtraModel> {
        const field: FormField<TareaOtraModel> = {
            name: options.name,
            vinculatedFields: undefined,
            field: options.name,
            label: options.label,
            input: this.createInput(options),
            width: options.width,
            conditions: this.conditions({
                isRequired: options.isRequired ?? false,
                isVisible: options.isVisible ?? true,
                isRememberedOnDuplicate: options.isRememberedOnDuplicate ?? true,
            }),
            order: options.order,
        };

        if (options.vinculatedFields !== undefined) {
            field.vinculatedFields = options.vinculatedFields;
        }

        if (options.dependencies !== undefined) {
            field.dependencies = options.dependencies;
        }

        if (options.computed !== undefined) {
            field.computed = options.computed;
        }

        if (options.updateValue !== undefined) {
            field.updateValue = options.updateValue;
        }

        return field;
    }

    private createInput(options: OtrasFieldOptions): Input<TareaOtraModel> {
        const input: Input<TareaOtraModel> = {
            type: options.inputType,
            isEditable: options.isEditable ?? this.formType < FormType.DELETION,
        };

        const defaultValue = this.getDefaultValue(options.name, options.defaultValue);

        if (defaultValue !== undefined) {
            input.defaultValue = defaultValue;
        }

        if (options.data !== undefined) {
            input.data = options.data;
        }

        if (options.style !== undefined) {
            input.style = options.style;
        }

        if (options.calendarConfiguration !== undefined) {
            input.calendarConfiguration = options.calendarConfiguration;
        }

        if (options.textAreaConfiguration !== undefined) {
            input.textAreaConfiguration = options.textAreaConfiguration;
        }

        if (options.textMaskConfiguration !== undefined) {
            input.textMaskConfiguration = options.textMaskConfiguration;
        }

        if (this.isDropdownInput(options.inputType)) {
            input.dropdownConfiguration = {
                ...(options.valuePrimaryKey !== undefined ? { valuePrimaryKey: options.valuePrimaryKey } : {}),
                ...(options.dropdownConfiguration ?? {}),
                ...(options.filter !== undefined ? { filter: options.filter } : {}),
            };
        }

        return input;
    }

    private createHiddenField(
        name: string,
        defaultValue: string | boolean | number | Date | TareaOtraModel | null | undefined,
        order: number,
        isRememberedOnDuplicate: boolean,
        isVisible = true,
    ): FormField<TareaOtraModel> {
        const options: OtrasFieldOptions = {
            name,
            label: '',
            inputType: InputType.HIDDEN,
            isEditable: false,
            isRememberedOnDuplicate,
            isVisible,
            width: 1,
            order,
        };

        if (defaultValue !== undefined) {
            options.defaultValue = defaultValue;
        }

        return this.createField(options);
    }

    private shouldShowClientSelector(): boolean {
        return this.canSeeClientes;
    }

    private showDetorresEvents(date: Date): void {
        if (this.appName !== 'detorres') {
            return;
        }

        const matches = this.eventos.filter((event) => {
            const [day = 0, month = 0, year = 0] = event.start.split('-').map(Number);

            return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
        });

        if (matches.length === 0) {
            return;
        }

        const message = matches.map((event, index) =>
            `Evento ${index + 1}:\n• Título: ${event.title}\n• Hora inicio: ${event.hora_inicio}\n• Hora fin: ${event.hora_fin}`,
        ).join('\n\n');

        alert(message);
    }

    private getDefaultValue(
        name: string,
        defaultValue: string | boolean | number | Date | TareaOtraModel | null | undefined,
    ): string | boolean | number | Date | TareaOtraModel | null | undefined {
        const modelValue = (this.model as Record<string, string | boolean | number | Date | TareaOtraModel | null | undefined>)[name];
        const value = modelValue !== undefined ? modelValue : defaultValue ?? null;

        if (name === 'fecha' || name === 'fecha_fin') {
            return this.normalizeDateValue(value);
        }

        return value;
    }

    private getSelectedClientId(): string | null {
        const selectedClientIds = this.getSelectedIds(
            getDropdownSelectedArray(this.clientes),
            this.model.id_cliente,
        );

        if (selectedClientIds.length > 0) {
            return selectedClientIds[0] ?? null;
        }

        const selectedFinca = getDropdownSelectedSingle(this.fincas);

        return String(selectedFinca?.id_cliente ?? StorageManager.getClient()?.id ?? '');
    }


    private isSectorOptionForSelectedFinca(optionValue: unknown, context: ReactiveDropdownDataSourceContext<TareaOtraModel>): boolean {
        const sector = optionValue as SectorModel | null;

        if (sector === null || sector === undefined) {
            return true;
        }

        const fincaId = getContextEntityId(context, 'id_finca', null);

        return fincaId !== null && String(sector.id_finca ?? '') === String(fincaId);
    }

    private isParcelaOptionForSelectedLocation(optionValue: unknown, context: ReactiveDropdownDataSourceContext<TareaOtraModel>): boolean {
        const parcela = optionValue as ParcelasModel | null;

        if (parcela === null || parcela === undefined) {
            return true;
        }

        if (parcela.activo?.toString() !== '1' && this.appName !== 'ava') {
            return false;
        }

        const sectorIds = getContextEntityIds(context, 'id_sector');

        if (sectorIds.length > 0) {
            return sectorIds.includes(String(parcela.id_sector ?? ''));
        }

        const fincaId = getContextEntityId(context, 'id_finca', null);

        return fincaId !== null && String(parcela.id_finca ?? '') === String(fincaId);
    }

    private filterFinca(it: FilteredValue<FincasModel>): boolean {
        const finca = it.value;

        if (finca == null) {
            return true;
        }

        const currentFincaIds = this.getSelectedIds(this.model.id_finca);

        if (currentFincaIds.includes(String(finca.id ?? ''))) {
            return true;
        }

        const selectedClientIds = this.getSelectedIds(
            getDropdownSelectedArray(this.clientes),
            this.model.id_cliente,
            StorageManager.getClient()?.id,
        );

        if (selectedClientIds.length === 0) {
            return true;
        }

        return selectedClientIds.includes(String(finca.id_cliente ?? ''));
    }

    private filterSector(it: FilteredValue<SectorModel>): boolean {
        const sector = it.value;

        if (sector == null) {
            return true;
        }

        const currentSectorIds = this.getSelectedIds(this.model.id_sector);

        if (currentSectorIds.includes(String(sector.id ?? ''))) {
            return true;
        }

        const selectedFincaIds = this.getSelectedIds(getDropdownSelectedArray(this.fincas), this.model.id_finca);

        if (selectedFincaIds.length === 0) {
            return false;
        }

        return selectedFincaIds.includes(String(sector.id_finca ?? ''));
    }

    private filterParcela(it: FilteredValue<ParcelasModel>): boolean {
        const parcela = it.value;

        if (parcela == null) {
            return true;
        }

        const currentParcelaIds = this.getSelectedIds(this.model.ids_parcelas);

        if (currentParcelaIds.includes(String(parcela.id ?? ''))) {
            return true;
        }

        if (parcela.activo?.toString() !== '1' && this.appName !== 'ava') {
            return false;
        }

        const selectedSectorIds = this.getSelectedIds(
            getDropdownSelectedArray(this.sectores),
            this.model.id_sector,
        );

        if (selectedSectorIds.length > 0) {
            return selectedSectorIds.includes(String(parcela.id_sector ?? ''));
        }

        const selectedFincaIds = this.getSelectedIds(getDropdownSelectedArray(this.fincas), this.model.id_finca);

        if (selectedFincaIds.length === 0) {
            return false;
        }

        return selectedFincaIds.includes(String(parcela.id_finca ?? ''));
    }

    private filterTrabajador(it: FilteredValue<MantenimientoModel>): boolean {
        return it.value?.activo?.toString() === '1';
    }

    private filterMaquinaria(it: FilteredValue<MaquinariaModel>): boolean {
        const maquinaria = it.value;

        if (maquinaria == null) {
            return true;
        }

        if (maquinaria.activo?.toString() !== '1') {
            return false;
        }

        if (this.appName === 'pyf') {
            return maquinaria.id_cliente === StorageManager.getClient()?.id;
        }

        return true;
    }

    private filterProducto(it: FilteredValue<ProductModel>): boolean {
        const product = it.value;

        if (product == null) {
            return true;
        }

        return this.appName !== 'detorres' || product.tipo === 'Jardinería';
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }

    private isDropdownInput(inputType: InputType): boolean {
        return [
            InputType.DROPDOWN,
            InputType.DROPDOWN_SEARCH,
            InputType.MULTISELECT_SEARCH,
            InputType.MULTISELECT,
        ].includes(inputType);
    }

    private normalizeModelDates(model: Partial<TareaOtraModel>): Partial<TareaOtraModel> {
        return {
            ...model,
            fecha: this.normalizeDateValue(model.fecha) ?? '',
            fecha_fin: this.normalizeDateValue(model.fecha_fin) ?? '',
        };
    }

    private normalizeDateValue(value: unknown): string | undefined {
        if (value === null || value === undefined || value === '') {
            return undefined;
        }

        if (value instanceof Date) {
            return this.formatDate(value);
        }

        const textValue = String(value).trim();
        const isoMatch = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(textValue);

        if (isoMatch !== null) {
            const year = isoMatch[1] ?? '';
            const month = isoMatch[2] ?? '';
            const day = isoMatch[3] ?? '';

            return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
        }

        const spanishMatch = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/.exec(textValue);

        if (spanishMatch !== null) {
            const day = spanishMatch[1] ?? '';
            const month = spanishMatch[2] ?? '';
            const year = spanishMatch[3] ?? '';

            return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
        }

        return textValue;
    }

    private formatDate(date: Date): string {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString();

        return `${day}/${month}/${year}`;
    }

    private normalizeTimeValue(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        if (value instanceof Date) {
            const hours = value.getHours().toString().padStart(2, '0');
            const minutes = value.getMinutes().toString().padStart(2, '0');

            return `${hours}:${minutes}`;
        }

        const textValue = String(value).trim();
        const timeMatch = /(\d{1,2}):(\d{1,2})/.exec(textValue);

        if (timeMatch === null) {
            return textValue;
        }

        const hours = timeMatch[1] ?? '';
        const minutes = timeMatch[2] ?? '';

        return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    }

    private timeToMinutes(value: string): number | null {
        const match = /^(\d{1,2}):(\d{1,2})$/.exec(value);

        if (match === null) {
            return null;
        }

        const hours = match[1] ?? '';
        const minutes = match[2] ?? '';
        const hourNumber = Number(hours);
        const minuteNumber = Number(minutes);

        if (!Number.isFinite(hourNumber) || !Number.isFinite(minuteNumber)) {
            return null;
        }

        return hourNumber * 60 + minuteNumber;
    }

    private minutesToTime(totalMinutes: number): string {
        const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
        const minutes = (totalMinutes % 60).toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    private getSelectedIds(...values: unknown[]): string[] {
        return values
            .flatMap((value) => this.getIdsFromValue(value))
            .filter((value, index, array) => value.length > 0 && array.indexOf(value) === index);
    }

    private getIdsFromValue(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.flatMap((item) => this.getIdsFromValue(item));
        }

        if (value === null || value === undefined || value === '') {
            return [];
        }

        if (typeof value === 'object') {
            const id = (value as { id?: string | number | null }).id;

            return id === null || id === undefined || id === '' ? [] : [String(id)];
        }

        return String(value)
            .split(';')
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
    }
}
